import {
  CalendarDate,
  CalendarDateTime,
  getLocalTimeZone,
  ZonedDateTime,
} from "@internationalized/date";
import { getTimeZones } from "@vvo/tzdb";
import { startOfWeek, endOfWeek } from "date-fns";
import { type DateValue } from "react-aria";

export type MappedDateValue<T> = T extends ZonedDateTime
  ? ZonedDateTime
  : T extends CalendarDateTime
    ? CalendarDateTime
    : T extends CalendarDate
      ? CalendarDate
      : never;

export interface RangeValue<T> {
  start: T;
  end: T;
}

export interface TimeZoneOption {
  name: string;
  label: string;
  abbrev: string;
  offset: number;
}

// Range shortcut functions for date range picker
export type RangePeriod = "week" | "month" | "year";

export function getRangeByPeriod(period: RangePeriod) {
  let range: CalendarDate[] = [];
  if (period === "week") range = getThisWeekRange();
  else if (period === "month") range = getThisMonthRange();
  else if (period === "year") range = getThisYearRange();
  else return null;

  return { start: range[0], end: range[1] };
}

export function getThisWeekRange() {
  const now = new Date();
  const start = startOfWeek(now, { weekStartsOn: 1 });
  const end = endOfWeek(now, { weekStartsOn: 1 });
  return [
    new CalendarDate(start.getFullYear(), start.getMonth() + 1, start.getDate()),
    new CalendarDate(end.getFullYear(), end.getMonth() + 1, end.getDate()),
  ];
}

export function getThisMonthRange() {
  const now = new Date();
  return [
    new CalendarDate(now.getFullYear(), now.getMonth() + 1, 1),
    new CalendarDate(
      now.getFullYear(),
      now.getMonth() + 1,
      new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate(),
    ),
  ];
}

export function getThisYearRange() {
  const now = new Date();
  return [new CalendarDate(now.getFullYear(), 1, 1), new CalendarDate(now.getFullYear(), 12, 31)];
}

/**
 * Builds the options for the timezone select based on values from vvo/tzdb library
 */
export function useTimezoneOptions() {
  const timezones = getTimeZones({ includeUtc: true }).map((tz) => {
    return {
      name: tz.name,
      label: tz.alternativeName,
      abbrev: tz.abbreviation,
      offset: tz.currentTimeOffsetInMinutes,
    };
  });

  const userTimezoneRaw = getLocalTimeZone();
  const userTimezone = timezones.find((tz) => tz.name === userTimezoneRaw) || null;

  const uniqueTimezones = Array.from(new Map(timezones.map((tz) => [tz.label, tz])).values());

  const rest = uniqueTimezones
    .filter((tz) => tz.name !== userTimezone?.name)
    .sort((a, b) => a.offset - b.offset);

  const timezoneOptions: TimeZoneOption[] = userTimezone ? [userTimezone, ...rest] : rest;

  return {
    userTimezone,
    timezoneOptions,
  };
}

export function getOffsetForDate(timeZone: string, date: Date, locale: string): number {
  const dtf = new Intl.DateTimeFormat(locale, {
    timeZone,
    timeZoneName: "shortOffset",
  });

  const parts = dtf.formatToParts(date);
  const tzName = parts.find((p) => p.type === "timeZoneName")?.value;

  const match = tzName?.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
  if (!match) return 0;

  const sign = match[1] === "+" ? 1 : -1;
  const hours = parseInt(match[2], 10);
  const minutes = parseInt(match[3] ?? "0", 10);
  const offset = sign * (hours * 60 + minutes);
  return offset * 60 * 1000;
}

/**
 * Converts a range of calendar date times to specified timezone
 */
export function rangeToZonedRange(
  start: CalendarDateTime,
  end: CalendarDateTime,
  timezone: TimeZoneOption,
  locale: string,
) {
  return {
    start: new ZonedDateTime(
      start.year,
      start.month,
      start.day,
      timezone.name,
      getOffsetForDate(timezone.name, new Date(Date.parse(start.toString())), locale),
      start.hour,
      start.minute,
    ).toAbsoluteString(),
    end: new ZonedDateTime(
      end.year,
      end.month,
      end.day,
      timezone.name,
      getOffsetForDate(timezone.name, new Date(Date.parse(end.toString())), locale),
      end.hour,
      end.minute,
      end.second,
    ).toAbsoluteString(),
  };
}

export function isDateTimeType(
  obj: CalendarDateTime | CalendarDate | ZonedDateTime,
): obj is CalendarDateTime {
  return obj && "second" in obj;
}

/**
 * Initialize year options and min/max date values
 * If no min/max is provided, it will default to the year options min/max year
 */
export function initializeYearOptions(
  yearOptions?: number[],
  minValue?: DateValue | null,
  maxValue?: DateValue | null,
) {
  const todayYear = new Date().getFullYear();

  const years = yearOptions ?? Array.from({ length: 21 }, (_, i) => todayYear - 10 + i);

  const startYear = yearOptions?.[0] ?? todayYear - 10;
  const endYear = yearOptions?.[yearOptions.length - 1] ?? todayYear + 10;

  const minDate = minValue ?? new CalendarDateTime(startYear, 1, 1);
  const maxDate = maxValue ?? new CalendarDateTime(endYear, 12, 31);

  return {
    years,
    minDate,
    maxDate,
  };
}

export const DEFAULT_LOCALE = "en-US";
